import { getSelectedCompanyId } from "../utils/tenantContext";
import { apiFetch } from "../api/client";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [policyAssignments, setPolicyAssignments] = useState({});
  const [companyAssignments, setCompanyAssignments] = useState({});

  const getRole = () => {
    return localStorage.getItem("role") || "client_admin";
  };

  const getCompanyId = () => {
    return getSelectedCompanyId() || "";
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const isSuperAdmin = () => {
    return getRole() === "super_admin";
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const role = getRole();
      const companyId = getCompanyId();

      if (
        role !== "super_admin" &&
        !companyId
      ) {
        throw new Error(
          "No authenticated tenant is available."
        );
      }

      const devicesPath =
        role === "super_admin"
          ? `/devices`
          : `/devices/company/${companyId}`;

      const devicesRes = await apiFetch(devicesPath, {
        headers: getAuthHeaders(),
      });

      let devicesData = null;

      try {
        devicesData = await devicesRes.json();
      } catch {
        devicesData = null;
      }

      if (!devicesRes.ok) {
        const message =
          devicesData?.detail ||
          devicesData?.message ||
          `Device request failed with status ${devicesRes.status}`;

        console.error("Devices API failure:", {
          url: devicesPath,
          status: devicesRes.status,
          response: devicesData,
          role,
          companyId,
          tokenPresent: Boolean(
            localStorage.getItem("access_token")
          ),
        });

        if (devicesRes.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("loggedIn");
          localStorage.removeItem("admin_id");
          localStorage.removeItem("username");
          localStorage.removeItem("role");
          localStorage.removeItem("company_id");

          window.location.assign("/login");
          return;
        }

        throw new Error(message);
      }

      const safeDevices = Array.isArray(devicesData)
        ? devicesData
        : [];

      setDevices(safeDevices);

      const policiesRes = await apiFetch(`/policies`, {
        headers: getAuthHeaders(),
      });
      const policiesData = policiesRes.ok ? await policiesRes.json() : [];
      const safePolicies = Array.isArray(policiesData) ? policiesData : [];

      setPolicies(safePolicies);

      if (isSuperAdmin()) {
        try {
          const companiesRes = await apiFetch(`/auth/list-companies`, {
            headers: getAuthHeaders(),
          });
          const companiesData = companiesRes.ok
            ? await companiesRes.json()
            : { companies: [] };

          const safeCompanies = Array.isArray(companiesData.companies)
            ? companiesData.companies
            : [];

          setCompanies(safeCompanies);
        } catch (companyError) {
          console.error("Companies load error:", companyError);
          setCompanies([]);
        }
      } else {
        setCompanies([]);
      }

      const initialPolicyAssignments = {};
      const initialCompanyAssignments = {};

      safeDevices.forEach((device) => {
        initialPolicyAssignments[device.id] =
          device.policy_id !== null && device.policy_id !== undefined
            ? String(device.policy_id)
            : "";

        initialCompanyAssignments[device.id] =
          device.company_id !== null && device.company_id !== undefined
            ? String(device.company_id)
            : "";
      });

      setPolicyAssignments(initialPolicyAssignments);
      setCompanyAssignments(initialCompanyAssignments);
    } catch (error) {
      console.error("Devices page load error:", error);
      setDevices([]);
      setPolicies([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const isOnline = (lastSeen) => {
    if (!lastSeen) return false;

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / 1000 / 60;

    return diffMinutes <= 10;
  };

  const groupedDevices = useMemo(() => {
    const grouped = {};

    devices.forEach((device) => {
      const companyName =
        device.company_name ||
        (device.company_id ? `Company ${device.company_id}` : "Unassigned");

      if (!grouped[companyName]) {
        grouped[companyName] = [];
      }

      grouped[companyName].push(device);
    });

    return grouped;
  }, [devices]);

  const totalDevices = devices.length;
  const onlineDevices = devices.filter((device) =>
    isOnline(device.last_seen)
  ).length;
  const offlineDevices = totalDevices - onlineDevices;

  const handlePolicyAssignmentChange = (deviceId, value) => {
    setPolicyAssignments((prev) => ({
      ...prev,
      [deviceId]: value,
    }));
  };

  const handleCompanyAssignmentChange = (deviceId, value) => {
    setCompanyAssignments((prev) => ({
      ...prev,
      [deviceId]: value,
    }));
  };

  const handleAssignPolicy = async (deviceId) => {
    try {
      const selectedPolicyId = policyAssignments[deviceId];

      const payload = {
        policy_id: selectedPolicyId ? parseInt(selectedPolicyId, 10) : null,
      };

      const res = await apiFetch(`/devices/${deviceId}/assign-policy`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.detail || data.error || "Failed to assign policy");
      }

      alert(payload.policy_id === null ? "Policy unassigned" : "Policy assigned");
      loadData();
    } catch (error) {
      console.error("Assign policy error:", error);
      alert(error.message);
    }
  };

  const handleAssignCompany = async (deviceId) => {
    if (!isSuperAdmin()) {
      alert("Only chief admins can reassign companies.");
      return;
    }

    try {
      const selectedCompanyId = companyAssignments[deviceId];

      const payload = {
        company_id: selectedCompanyId ? parseInt(selectedCompanyId, 10) : null,
      };

      const res = await apiFetch(`/devices/${deviceId}/assign-company`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.detail || data.error || "Failed to assign company");
      }

      alert(payload.company_id === null ? "Company unassigned" : "Company assigned");
      loadData();
    } catch (error) {
      console.error("Assign company error:", error);
      alert(error.message);
    }
  };

  const handleDecommissionDevice = async (device) => {
    const deviceId = device?.id;
    const hostname = device?.hostname || device?.device_name || "Unknown Device";

    const confirmed = window.confirm(
      `Decommission device "${hostname}"?

` +
        "This revokes the endpoint credential and marks the device decommissioned/offline. " +
        "It does NOT remove Wireless Press software from the endpoint."
    );

    if (!confirmed) return;

    try {
      const res = await apiFetch(`/devices/${deviceId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok || data?.error) {
        throw new Error(
          data?.detail || data?.error || "Failed to decommission device"
        );
      }

      alert(
        "Device decommissioned successfully. Endpoint software was not removed."
      );
      loadData();
    } catch (error) {
      console.error("Decommission device error:", error);
      alert(error.message);
    }
  };

  const handleRequestRemoteUninstall = async (device) => {
    const platform = String(device?.platform || "").toLowerCase();
    const hostname = device?.hostname || device?.device_name || "Unknown Device";

    if (!platform.includes("windows")) {
      alert(
        "Remote uninstall is currently supported only for Windows endpoints. " +
          "Use Decommission to revoke this endpoint without implying software removal."
      );
      return;
    }

    if (String(device?.lifecycle_state || "active").toLowerCase() !== "active") {
      alert("Only active endpoints can receive a remote uninstall request.");
      return;
    }

    const reason = window.prompt(
      `Reason for remotely uninstalling Wireless Press from "${hostname}"?`,
      "Administrative endpoint removal"
    );

    if (reason === null) return;

    const normalizedReason = reason.trim();

    if (!normalizedReason) {
      alert("A remote uninstall reason is required.");
      return;
    }

    if (normalizedReason.length > 500) {
      alert("The remote uninstall reason must be 500 characters or fewer.");
      return;
    }

    const payload = {
      device_id: Number(device.id),
      reason: normalizedReason,
      approval_ttl_minutes: 60,
    };

    if (isSuperAdmin()) {
      const targetCompanyId = Number(device.company_id || getCompanyId());

      if (!targetCompanyId) {
        alert("The target company could not be resolved for this endpoint.");
        return;
      }

      payload.company_id = targetCompanyId;
    }

    const confirmed = window.confirm(
      `Create a remote uninstall request for "${hostname}"?

` +
        "This does not execute immediately. A distinct second administrator must approve the request before the Windows endpoint can execute it."
    );

    if (!confirmed) return;

    try {
      const res = await apiFetch("/remote-uninstall/requests", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.error ||
            `Remote uninstall request failed with status ${res.status}`
        );
      }

      alert(
        `Remote uninstall request ${data?.request_uuid || "created"}. ` +
          "A distinct second administrator must approve it before execution."
      );

      loadData();
    } catch (error) {
      console.error("Remote uninstall request error:", error);
      alert(error.message);
    }
  };

  return (
    <div>
      <div className="page-actions">
        <h2>Devices</h2>

        <button className="primary-button" onClick={loadData}>
          Refresh
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div className="table-card" style={{ padding: "18px" }}>
          <h4>Total Devices</h4>
          <h2>{totalDevices}</h2>
        </div>

        <div className="table-card" style={{ padding: "18px" }}>
          <h4>Online</h4>
          <h2>{onlineDevices}</h2>
        </div>

        <div className="table-card" style={{ padding: "18px" }}>
          <h4>Offline</h4>
          <h2>{offlineDevices}</h2>
        </div>

        <div className="table-card" style={{ padding: "18px" }}>
          <h4>{isSuperAdmin() ? "Role" : "Company ID"}</h4>
          <h2>{isSuperAdmin() ? "Chief Admin" : getCompanyId()}</h2>
        </div>
      </div>

      {loading ? (
        <div className="table-card">
          <p>Loading devices...</p>
        </div>
      ) : Object.keys(groupedDevices).length > 0 ? (
        Object.entries(groupedDevices).map(([companyName, companyDevices]) => (
          <div
            className="table-card"
            key={companyName}
            style={{ marginBottom: "24px" }}
          >
            <h3 style={{ marginBottom: "16px" }}>{companyName}</h3>

            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Hostname</th>
                  <th>UUID</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Last Seen</th>
                  <th>Policy</th>
                  {isSuperAdmin() && <th>Company</th>}
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {companyDevices.map((device) => (
                  <tr key={device.id}>
                    <td>{device.id}</td>

                    <td>
                      <Link to={`/device/${device.id}`}>
                        {device.hostname || device.device_name || "N/A"}
                      </Link>
                    </td>

                    <td>{device.device_uuid || "N/A"}</td>

                    <td>{device.platform || "N/A"}</td>

                    <td>
                      <span
                        className={`severity-badge ${
                          isOnline(device.last_seen)
                            ? "severity-low"
                            : "severity-high"
                        }`}
                      >
                        {isOnline(device.last_seen) ? "Online" : "Offline"}
                      </span>
                    </td>

                    <td>{device.last_seen || "N/A"}</td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <select
                          value={policyAssignments[device.id] || ""}
                          onChange={(e) =>
                            handlePolicyAssignmentChange(
                              device.id,
                              e.target.value
                            )
                          }
                          className="filter-select"
                        >
                          <option value="">No Policy</option>

                          {policies.map((policy) => (
                            <option key={policy.id} value={policy.id}>
                              {policy.name}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => handleAssignPolicy(device.id)}
                        >
                          Save Policy
                        </button>
                      </div>
                    </td>

                    {isSuperAdmin() && (
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <select
                            value={companyAssignments[device.id] || ""}
                            onChange={(e) =>
                              handleCompanyAssignmentChange(
                                device.id,
                                e.target.value
                              )
                            }
                            className="filter-select"
                          >
                            <option value="">No Company</option>

                            {companies.map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.company_name}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            onClick={() => handleAssignCompany(device.id)}
                          >
                            Save Company
                          </button>
                        </div>
                      </td>
                    )}

                    <td>
                      {String(device.lifecycle_state || "active").toLowerCase() ===
                      "decommissioned" ? (
                        <span>Decommissioned</span>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {String(device.platform || "")
                            .toLowerCase()
                            .includes("windows") && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRequestRemoteUninstall(device)
                              }
                            >
                              Request Uninstall
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleDecommissionDevice(device)}
                          >
                            Decommission
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <div className="table-card">
          <p>No devices found.</p>
        </div>
      )}
    </div>
  );
}

export default DevicesPage;